import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Filter, Plus, Search, Trash2, Layers, Eye, EyeOff, Archive, ArchiveRestore, ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { api, fileUrl, fmtEur, getErrorMessage } from "../../api";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import { toast } from "sonner";
import ConfirmDialog from "../../components/ConfirmDialog";
import InlineNumberEditor from "../../components/InlineNumberEditor";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";

export default function Products() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sizeOpts, setSizeOpts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("active"); // active | published | draft | archived
  const [archivedCount, setArchivedCount] = useState(0);
  const [ownCross, setOwnCross] = useState(false);
  const [propagationType, setPropagationType] = useState(""); // "" | "seed_derived" | "clone" | "none"
  const [lowStock, setLowStock] = useState(false);
  const [sort, setSort] = useState("");       // "" = default (newest first) | "name" | "size" | "price" | "stock"
  const [dir, setDir] = useState("asc");     // "asc" | "desc"
  const [confirmDel, setConfirmDel] = useState(null);
  const [archiveOffer, setArchiveOffer] = useState(null); // { product, orderCount, message } — shown when delete is blocked by orders
  const [selected, setSelected] = useState(new Set());
  const [tierTemplates, setTierTemplates] = useState([]);
  const [bulkTier, setBulkTier] = useState("");

  useEffect(() => { api.get("/tier-templates").then((r) => setTierTemplates(r.data)); }, []);

  // Keep the Archived count fresh on the tab badge — poll on load + after status changes.
  useEffect(() => {
    const refresh = () => api.get("/products?status=archived").then((r) => setArchivedCount(r.data.length)).catch(() => {});
    refresh();
    return () => {};
  }, [items]);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (category) params.set("category_id", category);
    if (status) params.set("status", status);
    if (ownCross) params.set("is_own_cross", "true");
    if (propagationType) params.set("propagation_type", propagationType);
    if (lowStock) params.set("low_stock", "true");
    if (sort) { params.set("sort", sort); params.set("dir", dir); }
    try {
      const { data } = await api.get(`/products?${params.toString()}`);
      setItems(data);
    } finally { setLoading(false); }
  }, [q, category, status, ownCross, propagationType, lowStock, sort, dir]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/size-options").then((r) => setSizeOpts(r.data));
  }, []);

  const sizeName = (id) => sizeOpts.find((s) => s.id === id)?.name || "size";
  const catName = (id) => categories.find((c) => c.id === id)?.name || "—";

  const patchVariant = async (variantId, field, rawValue, productId) => {
    const value = Number(rawValue);
    if (!Number.isFinite(value)) { toast.error("Enter a number"); throw new Error("invalid"); }
    if (field === "base_price" && value < 0) { toast.error("Price cannot be negative"); throw new Error("invalid"); }
    if (field === "stock_qty" && value < 0) { toast.error("Stock cannot be negative"); throw new Error("invalid"); }
    try {
      if (field === "base_price") {
        await api.patch(`/size-variants/${variantId}/price`, { base_price: value });
      } else if (field === "stock_qty") {
        await api.patch(`/size-variants/${variantId}/stock`, { stock_qty: value });
      }
      // optimistic update
      setItems((prev) => prev.map((p) => p.id !== productId ? p : {
        ...p, size_variants: p.size_variants.map((v) => v.id === variantId ? { ...v, [field]: value } : v),
      }));
      toast.success("Saved");
    } catch (err) {
      toast.error(getErrorMessage(err, "Save failed"));
      throw err;
    }
  };

  const duplicate = async (id) => {
    try {
      const { data } = await api.post(`/products/${id}/duplicate`);
      toast.success(`Duplicated → ${data.name}`);
      load();
    } catch (err) { toast.error("Duplicate failed"); }
  };

  const del = async (product) => {
    if (!product) return;
    try {
      await api.delete(`/products/${product.id}`);
      toast.success("Deleted");
      load();
      setConfirmDel(null);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      // 409 with structured detail = product referenced by orders. Offer archive instead.
      if (status === 409 && typeof detail === "object" && detail?.can_archive) {
        setConfirmDel(null);
        setArchiveOffer({
          product,
          orderCount: detail.referenced_by_orders || 0,
          message: detail.message || "This product is referenced by existing orders.",
        });
        return;
      }
      toast.error(getErrorMessage(err, "Delete failed"));
      setConfirmDel(null);
    }
  };

  const archive = async (product) => {
    if (!product) return;
    try {
      await api.post(`/products/${product.id}/archive`);
      toast.success(`Archived "${product.name}"`);
      setArchiveOffer(null);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err, "Archive failed"));
    }
  };

  const unarchive = async (product) => {
    try {
      await api.post(`/products/${product.id}/unarchive`);
      toast.success(`Restored "${product.name}" to draft`);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err, "Restore failed"));
    }
  };

  const toggleStatus = async (p) => {
    const next = p.status === "published" ? "draft" : "published";
    try {
      await api.patch(`/products/${p.id}`, { status: next });
      setItems((prev) => prev.map((it) => it.id === p.id ? { ...it, status: next } : it));
    } catch (err) { toast.error("Failed"); }
  };

  const toggleCross = async (p) => {
    const next = !p.is_own_cross;
    try {
      await api.patch(`/products/${p.id}`, { is_own_cross: next });
      setItems((prev) => prev.map((it) => it.id === p.id ? { ...it, is_own_cross: next } : it));
    } catch (err) { toast.error("Failed"); }
  };

  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const clearSel = () => setSelected(new Set());
  const allSelected = items.length > 0 && items.every((p) => selected.has(p.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(items.map((p) => p.id)));

  const bulk = async (action, extra = {}) => {
    if (selected.size === 0) return;
    try {
      await api.post("/admin/products/bulk", { action, product_ids: [...selected], ...extra });
      toast.success("Bulk action applied");
      clearSel(); load();
    } catch (err) { toast.error(getErrorMessage(err, "Failed")); }
  };

  const bulkExport = () => {
    const ids = [...selected];
    const params = ids.length ? `?ids=${ids.join(",")}` : "";
    const token = localStorage.getItem("db_token");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/csv/products${params}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob); a.download = "products.csv"; a.click();
      });
  };

  return (
    <>
      <PageHeader
        title="Products"
        subtitle="Inline-edit price and stock. Search and filter to keep the list tight."
        testid="page-products"
        actions={
          <Link to="/admin/products/new" data-testid="product-new-btn">
            <Button className="h-9 bg-[#2D6A4F] hover:bg-[#1B4332] text-white"><Plus size={14} /> New product</Button>
          </Link>
        }
      />
      <div className="p-4 sm:p-6">
        {/* Status tabs — owner UX: main screen never mixes in archived.
            Archived lives on its own tab with a count badge. */}
        <div className="flex flex-wrap items-center gap-1 mb-3 border-b border-[#E2E4E0]" data-testid="product-status-tabs">
          {[
            ["active", "Active"],
            ["published", "Published"],
            ["draft", "Draft"],
            ["archived", "Archived"],
          ].map(([k, label]) => {
            const isActive = status === k;
            return (
              <button
                key={k}
                data-testid={`product-tab-${k}`}
                onClick={() => setStatus(k)}
                className={`h-9 px-3 text-sm inline-flex items-center gap-1.5 border-b-2 -mb-px transition-colors ${
                  isActive
                    ? "border-[#2D6A4F] text-[#1B4332] font-semibold"
                    : "border-transparent text-[#5C665A] hover:text-[#1A1D1A]"
                }`}
              >
                {label}
                {k === "archived" && archivedCount > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] bg-neutral-200 text-neutral-700" data-testid="product-tab-archived-count">
                    {archivedCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="relative w-full sm:w-64">
            <Search size={14} className="absolute left-2 top-2.5 text-[#5C665A]" />
            <Input data-testid="product-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name…" className="pl-7 h-9 w-full" />
          </div>
          <Select value={category || "__all__"} onValueChange={(v) => setCategory(v === "__all__" ? "" : v)}>
            <SelectTrigger className="h-9 w-full sm:w-40" data-testid="product-filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All categories</SelectItem>
              {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <label className="inline-flex items-center gap-2 text-sm text-[#1A1D1A] cursor-pointer h-9 px-2 rounded-md hover:bg-white">
            <Checkbox data-testid="product-filter-cross" checked={ownCross} onCheckedChange={(v) => setOwnCross(!!v)} />
            DocBlock Cross
          </label>
          <Select value={propagationType || "__all__"} onValueChange={(v) => setPropagationType(v === "__all__" ? "" : v)}>
            <SelectTrigger className="h-9 w-full sm:w-40" data-testid="product-filter-propagation"><SelectValue placeholder="Propagation" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All propagation</SelectItem>
              <SelectItem value="seed_derived">Seed Derived</SelectItem>
              <SelectItem value="clone">Clone</SelectItem>
              <SelectItem value="none">Untagged</SelectItem>
            </SelectContent>
          </Select>
          <label className="inline-flex items-center gap-2 text-sm text-[#1A1D1A] cursor-pointer h-9 px-2 rounded-md hover:bg-white">
            <Checkbox data-testid="product-filter-low-stock" checked={lowStock} onCheckedChange={(v) => setLowStock(!!v)} />
            Low stock (≤5)
          </label>
          {(q || category || ownCross || propagationType || lowStock) && (
            <button className="text-sm text-[#5C665A] hover:text-[#1A1D1A] inline-flex items-center gap-1" onClick={() => { setQ(""); setCategory(""); setOwnCross(false); setPropagationType(""); setLowStock(false); }} data-testid="product-filter-clear">
              <Filter size={14} /> Clear
            </button>
          )}
          {/* Mobile-only sort control (desktop uses clickable headers below). */}
          <div className="md:hidden inline-flex items-center gap-1" data-testid="product-sort-mobile-wrap">
            <Select
              value={sort ? `${sort}:${dir}` : "__default__"}
              onValueChange={(v) => {
                if (v === "__default__") { setSort(""); setDir("asc"); return; }
                const [k, d] = v.split(":"); setSort(k); setDir(d);
              }}
            >
              <SelectTrigger className="h-9 w-44" data-testid="product-sort-mobile"><SelectValue placeholder="Sort" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__default__">Default (newest)</SelectItem>
                <SelectItem value="name:asc">Name A → Z</SelectItem>
                <SelectItem value="name:desc">Name Z → A</SelectItem>
                <SelectItem value="size:asc">Size · smallest first</SelectItem>
                <SelectItem value="size:desc">Size · largest first</SelectItem>
                <SelectItem value="price:asc">Price · lowest first</SelectItem>
                <SelectItem value="price:desc">Price · highest first</SelectItem>
                <SelectItem value="stock:asc">Stock · lowest first</SelectItem>
                <SelectItem value="stock:desc">Stock · highest first</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="sm:ml-auto text-sm text-[#5C665A]" data-testid="product-count">{items.length} product{items.length === 1 ? "" : "s"}</div>
        </div>

        <div className="bg-white border border-[#E2E4E0] rounded-lg overflow-hidden">
          {selected.size > 0 && (
            <div className="flex flex-wrap items-center gap-2 px-3 py-2 bg-[#E9F5EC] text-[#1B4332] border-b border-[#E2E4E0]" data-testid="products-bulkbar">
              <span className="text-sm font-medium">{selected.size} selected</span>
              <Button size="sm" onClick={() => bulk("publish")} className="h-8 bg-[#2D6A4F] hover:bg-[#1B4332] text-white ml-2" data-testid="bulk-publish"><Eye size={13} /> Publish</Button>
              <Button size="sm" onClick={() => bulk("unpublish")} variant="outline" className="h-8" data-testid="bulk-unpublish"><EyeOff size={13} /> Unpublish</Button>
              <div className="inline-flex items-center gap-1">
                <Select value={bulkTier} onValueChange={setBulkTier}>
                  <SelectTrigger className="h-8 w-40" data-testid="bulk-tier-select"><SelectValue placeholder="Tier template" /></SelectTrigger>
                  <SelectContent>{tierTemplates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
                <Button size="sm" disabled={!bulkTier} onClick={() => bulk("apply_tier", { tier_template_id: bulkTier })} variant="outline" className="h-8" data-testid="bulk-apply-tier"><Layers size={13} /> Apply</Button>
              </div>
              <Button size="sm" variant="outline" onClick={bulkExport} className="h-8" data-testid="bulk-export">Export CSV</Button>
              <button className="ml-auto text-xs text-[#1B4332] hover:underline" onClick={clearSel} data-testid="bulk-clear">clear selection</button>
            </div>
          )}

          {/* Mobile: card list */}
          <div className="md:hidden divide-y divide-[#E2E4E0]" data-testid="products-card-list">
            {loading && <div className="px-3 py-8 text-center text-[#5C665A]" data-testid="products-loading-m">Loading…</div>}
            {!loading && items.length === 0 && <div className="px-3 py-8 text-center text-[#5C665A]" data-testid="products-empty-m">No products.</div>}
            {!loading && items.map((p) => (
              <div key={p.id} className="p-3" data-testid={`product-card-${p.id}`}>
                <div className="flex items-start gap-3">
                  <Checkbox checked={selected.has(p.id)} onCheckedChange={() => toggleSel(p.id)} data-testid={`product-select-m-${p.id}`} className="mt-1" />
                  <div className="w-12 h-12 rounded-md border border-[#E2E4E0] bg-[#F9F9F8] overflow-hidden shrink-0">
                    {p.photos?.[0] ? <img src={fileUrl(p.photos[0].storage_path)} alt="" className="w-full h-full object-cover" /> : null}
                  </div>
                  <div className="flex-1 min-w-0">
                    <Link to={`/admin/products/${p.id}`} className="font-medium hover:underline break-words" data-testid={`product-name-m-${p.id}`}>{p.name}</Link>
                    {p.is_own_cross && (p.seed_parent || p.pollen_parent) && (
                      <div className="text-xs text-[#5C665A] mt-0.5 break-words">
                        {(p.seed_parent?.name || "—")} × {(p.pollen_parent?.name || "—")}
                      </div>
                    )}
                    <div className="mt-1 flex items-center gap-2 text-xs">
                      <span className="text-[#5C665A]">{catName(p.category_id)}</span>
                      <button data-testid={`product-status-m-${p.id}`} onClick={() => toggleStatus(p)} disabled={p.status === "archived"}>
                        <Badge className={
                          p.status === "published" ? "bg-[#E9F5EC] text-[#1B4332] border-transparent"
                          : p.status === "archived" ? "bg-neutral-200 text-neutral-500 border-transparent"
                          : "bg-neutral-100 text-neutral-600 border-transparent"
                        }>
                          {p.status}
                        </Badge>
                      </button>
                      {p.is_own_cross && <Badge className="bg-[#E9F5EC] text-[#1B4332] border-transparent">Cross</Badge>}
                    </div>
                  </div>
                </div>
                <div className="mt-3 space-y-2">
                  {(p.size_variants || []).map((v) => (
                    <div key={v.id} className="flex items-center gap-2 border border-[#E2E4E0] rounded-md px-2 py-1.5" data-testid={`variant-card-${v.id}`}>
                      <span className="text-xs text-[#5C665A] w-14 shrink-0">{sizeName(v.size_option_id)}</span>
                      <label className="text-[10px] text-[#5C665A]">€</label>
                      <InlineNumberEditor
                        value={v.base_price}
                        step="0.01"
                        min={0}
                        onCommit={(val) => patchVariant(v.id, "base_price", val, p.id)}
                        inputClassName="db-inline w-20 min-h-[36px] text-right"
                        testid={`variant-price-m-${v.id}`}
                        ariaLabel={`Base price for ${sizeName(v.size_option_id)}`}
                      />
                      <label className="text-[10px] text-[#5C665A] ml-auto">stock</label>
                      <InlineNumberEditor
                        value={v.stock_qty}
                        step="1"
                        min={0}
                        onCommit={(val) => patchVariant(v.id, "stock_qty", val, p.id)}
                        inputClassName={`db-inline w-16 min-h-[36px] text-right ${v.stock_qty <= 5 ? "text-[#C94A4A]" : ""}`}
                        testid={`variant-stock-m-${v.id}`}
                        ariaLabel={`Stock for ${sizeName(v.size_option_id)}`}
                      />
                    </div>
                  ))}
                  {(!p.size_variants || p.size_variants.length === 0) && <div className="text-xs text-[#5C665A]">no sizes</div>}
                </div>
                <div className="mt-2 flex items-center gap-2 text-xs">
                  {p.status === "archived" ? (
                    <button className="text-[#2D6A4F] hover:bg-[#E9F5EC] inline-flex items-center gap-1 min-h-[36px] px-2 rounded-md border border-[#E2E4E0]" onClick={() => unarchive(p)} data-testid={`product-restore-m-${p.id}`}>
                      <ArchiveRestore size={14} /> Restore
                    </button>
                  ) : (
                    <button className="text-[#5C665A] hover:bg-[#F9F9F8] inline-flex items-center gap-1 min-h-[36px] px-2 rounded-md border border-[#E2E4E0]" onClick={() => duplicate(p.id)} data-testid={`product-duplicate-m-${p.id}`}>
                      <Copy size={14} /> Duplicate
                    </button>
                  )}
                  <button className="text-[#C94A4A] hover:bg-[#FDF3F3] inline-flex items-center gap-1 min-h-[36px] px-2 rounded-md border border-[#f2c7c7]" onClick={() => setConfirmDel(p)} data-testid={`product-delete-m-${p.id}`}>
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: table */}
          <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F9F9F8] text-[#5C665A]">
              <tr>
                <th className="px-3 py-2 w-8"><Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="products-select-all" /></th>
                <th className="text-left px-3 py-2 db-h font-semibold w-14"></th>
                <th className="text-left px-3 py-2 db-h font-semibold">
                  <SortHeader
                    label="Product"
                    sortKey="name"
                    activeKey={sort}
                    dir={dir}
                    onChange={(k, d) => { setSort(k); setDir(d); }}
                    tooltip="A → Z, case- and symbol-insensitive (® and quotes ignored, '9' naturally before '14')."
                    testid="product-sort-name"
                  />
                </th>
                <th className="text-left px-3 py-2 db-h font-semibold w-32">Category</th>
                <th className="text-left px-3 py-2 db-h font-semibold">
                  <div className="inline-flex items-center gap-2 flex-wrap">
                    <span>Sizes · price · stock</span>
                    <SortChip
                      label="Size"
                      sortKey="size"
                      activeKey={sort}
                      dir={dir}
                      onChange={(k, d) => { setSort(k); setDir(d); }}
                      tooltip="Sorts by each product's primary (smallest sort-order) size option."
                      testid="product-sort-size"
                    />
                    <SortChip
                      label="Price"
                      sortKey="price"
                      activeKey={sort}
                      dir={dir}
                      onChange={(k, d) => { setSort(k); setDir(d); }}
                      tooltip="Sorts by each product's LOWEST base price across its sizes."
                      testid="product-sort-price"
                    />
                    <SortChip
                      label="Stock"
                      sortKey="stock"
                      activeKey={sort}
                      dir={dir}
                      onChange={(k, d) => { setSort(k); setDir(d); }}
                      tooltip="Sorts by TOTAL stock across sizes."
                      testid="product-sort-stock"
                    />
                  </div>
                </th>
                <th className="text-center px-3 py-2 db-h font-semibold w-24">Cross</th>
                <th className="text-center px-3 py-2 db-h font-semibold w-28">Status</th>
                <th className="px-3 py-2 w-28"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="px-3 py-8 text-center text-[#5C665A]" data-testid="products-loading">Loading…</td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={8} className="px-3 py-8 text-center text-[#5C665A]" data-testid="products-empty">No products.</td></tr>}
              {!loading && items.map((p) => (
                <tr key={p.id} className="border-t border-[#E2E4E0] db-row" data-testid={`product-row-${p.id}`}>
                  <td className="px-3 py-2"><Checkbox checked={selected.has(p.id)} onCheckedChange={() => toggleSel(p.id)} data-testid={`product-select-${p.id}`} /></td>
                  <td className="px-3 py-2">
                    <div className="w-10 h-10 rounded-md border border-[#E2E4E0] bg-[#F9F9F8] overflow-hidden">
                      {p.photos?.[0] ? (
                        <img src={fileUrl(p.photos[0].storage_path)} alt="" className="w-full h-full object-cover" />
                      ) : null}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <Link to={`/admin/products/${p.id}`} className="font-medium hover:underline" data-testid={`product-name-${p.id}`}>{p.name}</Link>
                    {p.is_own_cross && (p.seed_parent || p.pollen_parent) && (
                      <div className="text-xs text-[#5C665A]">
                        {(p.seed_parent?.name || "—")} × {(p.pollen_parent?.name || "—")}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-[#5C665A]">{catName(p.category_id)}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {(p.size_variants || []).map((v) => (
                        <div key={v.id} className="inline-flex items-center gap-1 border border-[#E2E4E0] rounded-md pl-2 pr-1 py-0.5" data-testid={`variant-chip-${v.id}`}>
                          <span className="text-xs text-[#5C665A]">{sizeName(v.size_option_id)}</span>
                          <InlineNumberEditor
                            value={v.base_price}
                            step="0.01"
                            min={0}
                            onCommit={(val) => patchVariant(v.id, "base_price", val, p.id)}
                            inputClassName="db-inline w-20"
                            testid={`variant-price-${v.id}`}
                            ariaLabel="Base price EUR"
                          />
                          <span className="text-xs text-[#5C665A]">×</span>
                          <InlineNumberEditor
                            value={v.stock_qty}
                            step="1"
                            min={0}
                            onCommit={(val) => patchVariant(v.id, "stock_qty", val, p.id)}
                            inputClassName={`db-inline w-14 ${v.stock_qty <= 5 ? "text-[#C94A4A]" : ""}`}
                            testid={`variant-stock-${v.id}`}
                            ariaLabel="Stock quantity"
                          />
                        </div>
                      ))}
                      {(!p.size_variants || p.size_variants.length === 0) && <span className="text-xs text-[#5C665A]">no sizes</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-center">
                    <button data-testid={`product-cross-${p.id}`} onClick={() => toggleCross(p)} className={`inline-flex h-6 w-11 rounded-full transition-colors ${p.is_own_cross ? "bg-[#2D6A4F]" : "bg-neutral-200"}`} title="DocBlock Cross">
                      <span className={`inline-block h-5 w-5 mt-0.5 rounded-full bg-white shadow transition-transform ${p.is_own_cross ? "translate-x-5" : "translate-x-0.5"}`} />
                    </button>
                  </td>
                  <td className="px-3 py-2 text-center">
                    <button data-testid={`product-status-${p.id}`} onClick={() => toggleStatus(p)} disabled={p.status === "archived"} title={p.status === "archived" ? "Restore first" : "Toggle publish"}>
                      <Badge className={
                        p.status === "published" ? "bg-[#E9F5EC] text-[#1B4332] border-transparent"
                        : p.status === "archived" ? "bg-neutral-200 text-neutral-500 border-transparent"
                        : "bg-neutral-100 text-neutral-600 border-transparent"
                      }>
                        {p.status}
                      </Badge>
                    </button>
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {p.status === "archived" ? (
                      <button className="text-[#2D6A4F] hover:bg-[#E9F5EC] inline-flex items-center gap-1 px-2 h-7 rounded-md text-xs" onClick={() => unarchive(p)} data-testid={`product-restore-${p.id}`} title="Restore to draft">
                        <ArchiveRestore size={14} /> Restore
                      </button>
                    ) : (
                      <button className="text-[#5C665A] hover:bg-[#F9F9F8] inline-flex items-center px-1.5 h-7 rounded-md" onClick={() => duplicate(p.id)} data-testid={`product-duplicate-${p.id}`} title="Duplicate">
                        <Copy size={14} />
                      </button>
                    )}
                    <button className="text-[#C94A4A] hover:bg-[#FDF3F3] inline-flex items-center px-1.5 h-7 rounded-md ml-1" onClick={() => setConfirmDel(p)} data-testid={`product-delete-${p.id}`} title="Delete">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      </div>
      <ConfirmDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}
        title="Delete product?"
        description={confirmDel ? `This will remove "${confirmDel.name}" and all its size variants.` : ""}
        confirmLabel="Delete" destructive onConfirm={() => del(confirmDel)} testid="product-confirm-delete" />

      {/* Archive-offer dialog: shown when delete is blocked because the product
          has order history. Owner can archive (soft-delete) to remove from
          catalog while preserving all invoices. */}
      <AlertDialog open={!!archiveOffer} onOpenChange={(o) => !o && setArchiveOffer(null)}>
        <AlertDialogContent data-testid="product-archive-offer">
          <AlertDialogHeader>
            <AlertDialogTitle className="db-h">Can't delete — order history exists</AlertDialogTitle>
            <AlertDialogDescription>
              <span data-testid="product-archive-offer-message">{archiveOffer?.message}</span>
              <br /><br />
              Archiving hides <strong>“{archiveOffer?.product?.name}”</strong> from the buyer catalog and admin lists (unless you filter for Archived) while keeping every past invoice intact. You can restore it any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="product-archive-offer-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="product-archive-offer-confirm"
              onClick={() => archive(archiveOffer?.product)}
              className="bg-[#2D6A4F] hover:bg-[#1B4332] text-white inline-flex items-center gap-1"
            >
              <Archive size={14} /> Archive instead
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}


/**
 * Clickable table-header for primary (text) sortable columns.
 * Click cycles: inactive → asc → desc → asc on this column.
 * Switching to another column always starts at asc.
 */
function SortHeader({ label, sortKey, activeKey, dir, onChange, tooltip, testid }) {
  const active = activeKey === sortKey;
  const nextDir = !active ? "asc" : dir === "asc" ? "desc" : "asc";
  const Icon = !active ? ArrowUpDown : dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <button
      type="button"
      title={tooltip}
      onClick={() => onChange(sortKey, nextDir)}
      className={`inline-flex items-center gap-1 h-7 px-1 -mx-1 rounded hover:bg-[#EDECE7] transition-colors ${active ? "text-[#1A1D1A]" : ""}`}
      data-testid={testid}
      aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : "none"}
    >
      <span>{label}</span>
      <Icon size={12} className={active ? "text-[#1B4332]" : "opacity-50"} />
    </button>
  );
}

/**
 * Secondary pill-style sort control for columns that share a cell with other
 * sortable keys (Size/Price/Stock all live inside the "Sizes · price · stock"
 * header). Visually smaller than SortHeader so the containing title still
 * reads as the primary label.
 */
function SortChip({ label, sortKey, activeKey, dir, onChange, tooltip, testid }) {
  const active = activeKey === sortKey;
  const nextDir = !active ? "asc" : dir === "asc" ? "desc" : "asc";
  const Icon = !active ? ArrowUpDown : dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <button
      type="button"
      title={tooltip}
      onClick={() => onChange(sortKey, nextDir)}
      className={`inline-flex items-center gap-1 h-6 px-1.5 text-[11px] rounded border transition-colors ${
        active
          ? "border-[#2D6A4F] bg-[#E9F5EC] text-[#1B4332]"
          : "border-[#E2E4E0] bg-white text-[#5C665A] hover:border-[#B7C0B4] hover:text-[#1A1D1A]"
      }`}
      data-testid={testid}
      aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : "none"}
    >
      <span>{label}</span>
      <Icon size={10} className={active ? "" : "opacity-50"} />
    </button>
  );
}
